import { useEffect, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useDataRequestVirtualMachine } from '@vivli/features/data-requests/infrastructure/hook';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { useActiveUserContext, useAppConfigContext, useDataRequestPermissionsContext } from '@vivli/core/infrastructure/context';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { LoadIndicatorCenteredView } from '@vivli/shared/views';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { containerStyle, subContainerStyle } from './data-request-form.styles';
import { DataRequestFormRoutesComponent } from './data-request-form-routes.component';
import { useNavigate } from 'react-router';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { RequestHeaderContainerComponent } from '../../shared/request-header/request-header-container.component';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';

export const DataRequestFormComponent = () => {
    const { dataRequest, handleEditTitleClick, handleFormSave, handleDataPackageCreation } = useDataRequestContext();
    const [routes, setRoutes] = useState<{ route: string; title: string }[]>();
    const [hideReviewers, setHideReviewers] = useState(null);
    const { user, hasPermission } = useActiveUserContext();
    const formApi = useFormContext<IDataRequest>();
    const { isValid, isDirty } = useFormState();

    const navigate = useNavigate();
    const config = useAppConfigContext();
    const { organizations } = useOrganizations();
    const dataRequestVirtualMachine = useDataRequestVirtualMachine(dataRequest.id);
    const { userIsOwner, userIsAdmin, shouldHideReviewers } = useDataRequestPermissionsContext();

    const getRoutes = () => {
        let routes: { route: string; title: string }[] = [
            {
                route: 'researchProposal',
                title: 'Research Proposal'
            },
            {
                route: 'requestedStudies',
                title: 'Studies'
            },
            {
                route: 'statisticalAnalysisPlan',
                title: 'Statistical Analysis Plan'
            },
            {
                route: 'funding',
                title: 'Funding'
            },
            {
                route: 'otherInformation',
                title: 'Other Information / File Attachments'
            },
            {
                route: 'attestations',
                title: 'Attestations'
            }
        ];

        if (dataRequest.dataRequestHistoryEntries && dataRequest.dataRequestHistoryEntries.length > 0) {
            routes.push({ route: 'requestHistory', title: 'Request History' });
        }

        const leadTeamMember = dataRequest.researchTeam.researchTeamDetails.find((x) => x.userId === user.userId && x.isLeadResearcher);

        // allow owner or admin or lead researcher to modify team members
        //TODO PERMFE - redo with permissions+
        if ((userIsOwner(dataRequest) || hasPermission(PermissionsEnum.DrActAdv) || leadTeamMember) && !hideReviewers) {
            routes = [{ route: 'researchTeam', title: 'Research Team' }, ...routes];
        }

        if (dataRequest.status === DataRequestStatusEnum.Fulfilled && dataRequestVirtualMachine) {
            routes.push({ route: 'researchResults', title: 'Research Results' });
        }

        const shouldShowChat = config.chatEnabled && config.chatUri && config.chatUri.length > 0 && dataRequest.chatChannelId;
        if (shouldShowChat) {
            routes.push({ route: 'chat', title: 'Chat' });
        }

        //TODO PERMFE - redo with permissions+
        if (dataRequest.status === DataRequestStatusEnum.Draft && hasPermission(PermissionsEnum.DrActAdv)) {
            routes.push({ route: 'Notifications', title: 'Notifications' });
        }

        return routes;
    };

    useEffect(() => {
        if (!organizations) {
            return;
        }

        const permission = shouldHideReviewers(dataRequest, organizations);
        setHideReviewers(permission);
    }, [organizations]);

    useEffect(() => {
        if (hideReviewers === null) {
            return;
        }

        const activeRoutes = getRoutes();

        setRoutes(activeRoutes);
    }, [hideReviewers]);

    useEffect(() => {
        if (!isValid) {
            // force initial validation
            formApi.trigger();
        }

        //TODO PERMFE - redo with permissions+
        const isOwnerOrAdmin = userIsOwner(dataRequest) || userIsAdmin(dataRequest) || hasPermission(PermissionsEnum.DrActAdv);
        if (!isOwnerOrAdmin) {
            navigate('chat');
        }

        handleDataPackageCreation();
    }, []);
    if (!organizations || !routes) {
        return <LoadIndicatorCenteredView />;
    }
    return (
        <>
            <div style={containerStyle}>
                <div style={subContainerStyle}>
                    <RequestHeaderContainerComponent
                        onEditTitleClick={() => handleEditTitleClick()}
                        onSaveClick={() => handleFormSave(false)}
                        onSubmitClick={() => handleFormSave(true)}
                    />

                    <DataRequestFormRoutesComponent routes={routes} handleFormSave={() => handleFormSave(false)} />
                </div>
            </div>
        </>
    );
};
